import { useState } from 'react';
import type { useTemporaryMaterials, useCloudMaterials } from '../../../features/materials/hooks';
import { useMaterialUrls } from '../../../features/materials/useMaterialUrls';
import { GenerationInfoButton } from '../../materials/shared/GenerationInfoButton';
import { GenerationInfoModal } from '../../materials/shared/GenerationInfoModal';
import { hasGenerationInfo } from '../../materials/shared/generationInfoUtils';
import { Tooltip } from '../../common/Tooltip';

// プロンプト動画表示コンポーネント
type PromptVideoDisplayProps = {
  materialId: string;
  tempMaterials: ReturnType<typeof useTemporaryMaterials>;
  cloudMaterials: ReturnType<typeof useCloudMaterials>;
  onOpenPreview: (material: any) => void;
};

export function PromptVideoDisplay({ materialId, tempMaterials, cloudMaterials, onOpenPreview }: PromptVideoDisplayProps) {
  const [showGenerationInfo, setShowGenerationInfo] = useState(false);
  const tempItems = tempMaterials.data?.pages.flatMap(page => page.items) || [];
  const cloudItems = cloudMaterials.data?.pages.flatMap(page => page.items) || [];
  const allMaterials = [...tempItems, ...cloudItems];
  const material = allMaterials.find(m => m.id === materialId) ?? null;

  // 素材URLを用途別に解決（GCS署名付きURLも自動取得）
  const materialUrls = useMaterialUrls(material);

  if (!material) return null;

  const videoUrl = materialUrls?.video ?? null;
  const genMeta = (material as Record<string, unknown>).generation_metadata as Record<string, unknown> | undefined;
  const showInfoButton = hasGenerationInfo(genMeta);

  return (
    <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ width: 'fit-content', maxWidth: '100%' }}>
        {videoUrl ? (
          <video
            src={videoUrl}
            controls
            style={{
              display: 'block',
              maxWidth: '40vw',
              maxHeight: '70vh',
              borderRadius: '8px',
            }}
          />
        ) : (
          <div style={{ padding: '24px', textAlign: 'center', color: 'rgba(245, 241, 230, 0.6)' }}>
            動画を読み込み中です...
          </div>
        )}
        <div
          style={{
            marginTop: '12px',
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
          }}
        >
          <div style={{ justifySelf: 'start' }}>
            <Tooltip content="素材を開く" placement="bottom">
              <button
                type="button"
                onClick={() => onOpenPreview(material)}
                aria-label="素材を開く"
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  margin: '0 0 0 6px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                <img src="/enlarge.svg" alt="" style={{ width: '14px', height: '14px', display: 'block' }} />
              </button>
            </Tooltip>
          </div>
          <span style={{ fontSize: '11px', color: '#666', whiteSpace: 'nowrap' }}>個人フォルダに保存済み</span>
          <div style={{ justifySelf: 'end' }}>
            {showInfoButton && (
              <GenerationInfoButton onClick={() => setShowGenerationInfo(true)} />
            )}
          </div>
        </div>
      </div>
      {showGenerationInfo && (
        <GenerationInfoModal
          material={material}
          fullSizeImageUrl={materialUrls?.video ?? materialUrls?.fullsize ?? null}
          onClose={() => setShowGenerationInfo(false)}
        />
      )}
    </div>
  );
}
